<template>
  <BaseModal
    :title="title"
    :hookSufix="hookSufix"
    minHeight="200px"
    :onCloseHook="handleResetFields"
  >
    <template #content>
      <v-container class="pa-0">
        <v-row no-gutters v-if="currentStep === 'initial'">
          <v-col v-html="alertMessage"></v-col>
          <v-col cols="12" class="my-3">
            <b>{{ $t('message.pleaseConfirmRemove') }}</b>
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="removeText"
              :placeholder="$t('message.remove').toUpperCase()"
              dense
              outlined
              autofocus
              @keyup.enter="jumpToConfirmStep"
            >
            </v-text-field>
          </v-col>
        </v-row>
        <v-row no-gutters v-else>
          <v-col cols="12">
            Tem certeza de que deseja prosseguir com a exclusão? Essa ação não
            pode ser desfeita.
            <br />
            Todo o conteúdo selecionado deixará de existir na plataforma
          </v-col>
          <v-col cols="12">
            <v-checkbox
              v-model="checkboxConfirmation"
              class="mt-2"
              hide-details
            >
              <template v-slot:label>
                <span style="font-size: 14px">{{
                  $t('message.pages.video.deleteModal.checkboxText')
                }}</span>
              </template>
            </v-checkbox>
          </v-col>
          <v-col cols="12" class="my-3">
            <b>{{
              $t('message.pages.video.deleteModal.confirmRemoveMessage2')
            }}</b>
          </v-col>

          <v-col cols="12">
            <v-text-field
              v-model="password"
              class="pr-2"
              :label="$t('message.password')"
              dense
              outlined
              autofocus
              autocomplete="off"
              :error="errors.invalidAccountError"
              :messages="loginPasswordErrorMessage"
              :append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showPass ? 'text' : 'password'"
              @click:append="showPass = !showPass"
            >
              <template v-slot:message="{ message }">
                <span
                  class="red--text caption align-text"
                  v-if="errors.invalidAccountError"
                >
                  <v-icon color="red" size="18" class="pb-1">
                    mdi-alert-circle
                  </v-icon>
                  {{ message }}
                </span>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
      </v-container>
    </template>
    <template #card-actions>
      <v-card-actions class="px-0 py-8">
        <BaseBtn
          type="secondary"
          @click="$root.$emit(`close${hookSufix}`)"
          :disabled="isLoading || isFetchingPassword"
        >
          Cancelar
        </BaseBtn>
        <v-spacer></v-spacer>
        <template v-if="currentStep === 'confirm'">
          <BaseBtn
            @click="handleConfirmDelete"
            :disabled="
              !removeVerification ||
              isLoading ||
              password.length < 1 ||
              isFetchingPassword ||
              !checkboxConfirmation
            "
            :loading="isLoading || isFetchingPassword"
          >
            Confirmar
          </BaseBtn>
        </template>
        <template v-else-if="currentStep === 'initial'">
          <BaseBtn
            @click="jumpToConfirmStep"
            :disabled="!removeVerification || isLoading"
            :loading="isLoading"
          >
            Confirmar
          </BaseBtn>
        </template>
      </v-card-actions>
    </template>
  </BaseModal>
</template>

<script>
import BaseBtn from '@/components/BaseBtn.vue'
import BaseModal from '@/components/BaseModal.vue'
import { mapActions } from 'vuex'

export default {
  name: 'DefaultDeleteModal',
  components: {
    BaseBtn,
    BaseModal,
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    hookSufix: {
      type: String,
      required: true,
    },
    alertMessage: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    removeText: '',
    currentStep: 'initial', // initial | confirm
    password: '',
    checkboxConfirmation: false,
    showPass: false,
    errors: {
      invalidAccountError: false,
      unexpectedError: false,
      internalErrorException: false,
      removeError: false,
    },
    isFetchingPassword: false,
  }),
  computed: {
    userInfo() {
      return this.$store.getters['session/userAttributes']
    },
    removeVerification() {
      return (
        this.removeText.toUpperCase() ===
        this.$i18next.t('message.remove').toUpperCase()
      )
    },
    loginPasswordErrorMessage() {
      if (this.errors.invalidAccountError) {
        return this.$i18next.t(
          'message.pages.video.deleteModal.errors.invalidAccountError'
        )
      }
      if (this.errors.internalErrorException) {
        return this.$i18next.t(
          'message.pages.video.deleteModal.errors.internalError'
        )
      }
      if (this.errors.unexpectedError) {
        return (
          this.$i18next.t('message.errors.unexpectedError') + this.unknownError
        )
      }
      return ''
    },
  },
  methods: {
    ...mapActions('session', ['signInUser']),
    handleResetFields() {
      this.removeText = ''
      this.password = ''
      this.currentStep = 'initial'
    },
    async handleConfirmDelete() {
      this.isFetchingPassword = true
      let confirm = await this.confirmUserPassword()
      if (confirm) {
        this.$emit('confirmDelete')
      }
    },
    jumpToConfirmStep() {
      if (this.removeVerification) {
        this.currentStep = 'confirm'
      }
    },
    async confirmUserPassword() {
      try {
        await this.signInUser({
          username: this.userInfo.email,
          password: this.password,
        })
        this.isFetchingPassword = false
        return true
      } catch (error) {
        if (!error.code) {
          const anyError = JSON.parse(JSON.stringify(error))
          if (anyError.message.includes('Network Error')) {
            this.changePasswordLoading = false
            this.unknownError = 'Code: 500'
            this.newPasswordError.newPasswordError = true
            return
          }
        }
        if (
          error.code === 'NotAuthorizedException' ||
          error.code === 'InvalidParameterException'
        ) {
          this.errors.invalidAccountError = true
        }
        if (error.code === 'InternalErrorException') {
          this.errors.internalErrorException = true
        }
        this.isFetchingPassword = false
        return false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_ai_works';
</style>
